import type { FieldDefinition, ValidationIssue } from '@inside-us-aos/domain';

import styles from './NumberInput.module.css';
import { issueMessage } from './issueMessage';

export interface NumberInputProps {
  field: FieldDefinition;
  /**
   * Numeric value. `undefined` denotes "no answer" — the engine never
   * stores `NaN`. The primitive renders an empty string when value is
   * undefined.
   */
  value: number | undefined;
  issues: readonly ValidationIssue[];
  onChange: (next: number | undefined) => void;
  /**
   * Whether the user has interacted with the field. When `false`,
   * issues are suppressed. Defaults to `true` for backward
   * compatibility.
   */
  touched?: boolean;
  /** Fires on the input's native `onBlur`. */
  onBlur?: () => void;
}

/**
 * Catalog-driven number primitive. Renders `<input type="number">`
 * with `min` / `max` derived from the field's `min_value` / `max_value`
 * validators. Used today for biographic height/weight (e.g.,
 * `beneficiary.biographic.height_feet`, integer 1-9).
 *
 * Empty input emits `undefined` so the engine can distinguish "no
 * answer" from `0`. Non-empty parsed via `Number()`; `NaN` is filtered
 * out so the engine never sees an invalid number.
 */
export function NumberInput({
  field,
  value,
  issues,
  onChange,
  touched = true,
  onBlur,
}: NumberInputProps): JSX.Element {
  const inputId = `field-${field.id.replace(/\./g, '-')}`;
  const errorListId = `${inputId}-errors`;
  const showIssues = touched && issues.length > 0;
  const inputClassName = showIssues
    ? `${styles.field__input} ${styles['field__input--invalid']}`
    : styles.field__input;

  // Pull `min` / `max` / `step` from the catalog's numeric validators.
  // Default `min` to 0 — every numeric field in the USCIS catalog is
  // a count, age, year, income, or measurement, none of which are
  // negative. Catalog-declared `min_value` overrides the default when
  // explicitly set (e.g., a future field that legitimately accepts
  // negatives can declare `min_value: -X`).
  let minValue: number | undefined = 0;
  let maxValue: number | undefined;
  for (const rule of field.validation ?? []) {
    if (rule.type === 'min_value') minValue = rule.value;
    if (rule.type === 'max_value') maxValue = rule.value;
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const raw = event.target.value;
    if (raw === '') {
      onChange(undefined);
      return;
    }
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return;
    // Floor at `minValue` (default 0) so paste / spinner-down can't
    // sneak a negative value past the native attribute guard.
    if (minValue !== undefined && parsed < minValue) {
      onChange(minValue);
      return;
    }
    onChange(parsed);
  };

  return (
    <div className={styles.field} data-field-id={field.id}>
      <label htmlFor={inputId} className={styles.field__label}>
        {field.label}
        {field.required === true ? (
          <span className={styles['field__required-mark']}>*</span>
        ) : null}
      </label>

      <input
        id={inputId}
        type="number"
        value={value === undefined ? '' : value}
        onChange={handleChange}
        onBlur={onBlur}
        min={minValue}
        max={maxValue}
        step={1}
        aria-required={field.required === true || undefined}
        aria-invalid={showIssues || undefined}
        aria-describedby={showIssues ? errorListId : undefined}
        className={inputClassName}
      />

      {field.helpText !== undefined && field.helpText !== '' ? (
        <p className={styles['field__help-text']}>{field.helpText}</p>
      ) : null}

      {showIssues ? (
        <div role="alert">
          <ul id={errorListId} className={styles['field__error-list']}>
          {issues.map((issue, index) => (
            <li key={`${issue.code}-${index}`}>
              <span aria-hidden="true" className={styles['field__error-icon']}>
                !
              </span>
              {issueMessage(issue)}
            </li>
          ))}
        </ul>
        </div>
      ) : null}
    </div>
  );
}
